import { keyframes, styled } from "goober";
import { useState, type ReactNode } from "react";
import { spin } from "./motion";

// Must differ textually from `spin` so goober gives it a separate name, which is what restarts back-to-back turns.
const alternateSpin = keyframes`
  0% { rotate: 0deg; }
  100% { rotate: 360deg; }
`;

const Container = styled("span")`
  display: inline-flex;
  &[data-spinning="true"] {
    animation: ${spin} 700ms linear infinite;
  }
  &[data-turning="true"][data-turn-phase="first"] {
    animation: ${spin} 700ms linear;
  }
  &[data-turning="true"][data-turn-phase="alternate"] {
    animation: ${alternateSpin} 700ms linear;
  }
`;

export function RefreshSpin({
  spinning,
  turnKey,
  resting,
  children,
}: {
  spinning: boolean;
  turnKey?: number;
  resting?: ReactNode;
  children: ReactNode;
}) {
  const [animating, setAnimating] = useState(spinning);
  const [spinEnded, setSpinEnded] = useState(false);
  const [turnState, setTurnState] = useState({
    previousTurnKey: turnKey,
    remaining: 0,
    alternate: false,
  });

  if (spinning && !animating) {
    setAnimating(true);
    setSpinEnded(false);
  }
  // Reduced motion caps the spin at one short pass, so no later iteration arrives to stop on.
  if (!spinning && animating && spinEnded) {
    setAnimating(false);
    setSpinEnded(false);
  }
  if (turnKey !== turnState.previousTurnKey) {
    const addedTurns =
      turnKey !== undefined &&
      turnState.previousTurnKey !== undefined &&
      turnKey > turnState.previousTurnKey
        ? turnKey - turnState.previousTurnKey
        : 1;
    setTurnState({
      ...turnState,
      previousTurnKey: turnKey,
      remaining: turnState.remaining + addedTurns,
    });
  }

  const turning = turnState.remaining > 0;
  const active = animating || turning;

  return (
    <Container
      data-spinning={animating}
      data-turning={!animating && turning}
      data-turn-phase={turnState.alternate ? "alternate" : "first"}
      onAnimationIteration={(event) => {
        if (event.target === event.currentTarget && !spinning) {
          setAnimating(false);
        }
      }}
      onAnimationEnd={(event) => {
        if (event.target !== event.currentTarget) return;
        if (animating) {
          setSpinEnded(true);
          return;
        }
        setTurnState((state) => ({
          ...state,
          remaining: Math.max(0, state.remaining - 1),
          alternate: state.remaining > 1 ? !state.alternate : state.alternate,
        }));
      }}
    >
      {active ? children : (resting ?? children)}
    </Container>
  );
}
